export interface BlankCheckerOptions {
  /**
   * 白屏检测容器
   * @default ['html', 'body', '#app', '#root']
   */
  containers?: string[];
  /**
   * 是否启用骨架屏检测模式
   * @todo 生成骨架屏后需要立即调用下`snapshot`方法，将初始的骨架屏快照内容存储起来
   * @default false
   */
  skeleton?: boolean;
  /**
   * 是否开启调试模式
   * @default false
   */
  debug?: boolean;
  /**
   * 是否启用交叉采样检测，开启后检测的精确度会提高
   * @default
   */
  cross?: boolean;
  /**
   * 报告白屏异常，当检测到白屏后会调用此方法，可以在该回调中上报给服务端或三方的异常监控工具，如：Sentry
   * @requires
   */
  report: () => void;
}

interface SkeletonsType {
  initial: string[];
  current: string[];
}

const dpr = window.devicePixelRatio ?? 1;

const isValidString = (str: string) => typeof str === 'string' && str.length > 0;

export class BlankChecker {
  private containers: string[];
  private skeleton: boolean;
  private debug: boolean;
  private cross: boolean;
  private report: () => void;
  private canvas?: HTMLCanvasElement;
  private skeletons: SkeletonsType = {
    initial: [],
    current: [],
  };

  constructor(options: BlankCheckerOptions) {
    this.containers = options.containers ?? ['html', 'body', '#app', '#root'];
    this.skeleton = options.skeleton ?? false;
    this.debug = options.debug ?? false;
    this.cross = options.cross ?? false;
    this.report = options.report;
  }

  /**
   * 获取指定元素的选择器
   * @param element
   */
  private getSelector(element: Element) {
    if (isValidString(element.id)) {
      return `#${element.id}`;
    }

    if (isValidString(element.className)) {
      return `.${element.className.split(' ').filter(Boolean).join('.')}`;
    }

    return element.tagName.toLowerCase();
  }

  private getElementByPoint(x: number, y: number) {
    if (document.elementsFromPoint) {
      return document.elementsFromPoint(x, y)[0];
    } else {
      return document.elementFromPoint(x, y);
    }
  }

  private clearPoints(width: number, height: number) {
    if (this.canvas == null) {
      this.canvas = document.createElement('canvas');

      this.canvas.style.cssText = `
        position: absolute;
        left: 0;
        top: 0;
        width: ${width}px;
        height: ${height}px;
        pointer-events: none;
        z-index: 99999;
      `;
      this.canvas.width = width * dpr;
      this.canvas.height = height * dpr;

      document.body.appendChild(this.canvas);
    }

    this.canvas.getContext('2d')?.clearRect(0, 0, width, height);
  }

  private drawPoint(x: number, y: number) {
    if (this.canvas == null) {
      return;
    }

    const ctx = this.canvas.getContext('2d');

    if (ctx) {
      ctx.beginPath();
      ctx.arc(x * dpr, y * dpr, 2 * dpr, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();
    }
  }

  /**
   * 采样当前页面空白节点数量或骨架屏节点
   * @param initial 是否为初始采样
   */
  private sampling(initial = false) {
    // 每次采样前先清空上次的骨架屏选择器
    this.skeletons.current = [];

    const len = 10;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const totalPoints = len * (this.cross ? 4 : 2);
    let emptyPoints = 0;

    if (this.debug) {
      this.clearPoints(width, height);
    }

    /**
     * 标记当前元素是否为空白节点，骨架屏模式下存储首次或本次的骨架屏选择器用于最终对比其是否一致
     */
    const mark = (element: Element | null) => {
      if (element == null) {
        return;
      }

      const selector = this.getSelector(element);

      if (this.skeleton) {
        this.skeletons[initial ? 'initial' : 'current'].push(selector);
      } else if (this.containers.includes(selector)) {
        emptyPoints++;
      }
    };

    const offsetX = width / len / 2;
    const offsetY = height / len / 2;

    for (let i = 0; i < len; i++) {
      const x1 = (width * i) / len + offsetX;
      const y1 = height / 2;

      const l2rElement = this.getElementByPoint(x1, y1);
      mark(l2rElement);

      if (this.debug) {
        this.drawPoint(x1, y1);
      }

      const x2 = width / 2;
      const y2 = (height * i) / len + offsetY;

      const t2bElement = this.getElementByPoint(x2, y2);
      mark(t2bElement);

      if (this.debug) {
        this.drawPoint(x2, y2);
      }

      if (this.cross) {
        const x3 = (width * i) / len + offsetX;
        const y3 = (height * i) / len + offsetY;

        const tl2rbElement = this.getElementByPoint(x3, y3);
        mark(tl2rbElement);

        if (this.debug) {
          this.drawPoint(x3, y3);
        }

        const x4 = width - (width * i) / len - offsetX;
        const y4 = (height * i) / len + offsetY;

        const tr2lbElement = this.getElementByPoint(x4, y4);
        mark(tr2lbElement);

        if (this.debug) {
          this.drawPoint(x4, y4);
        }
      }
    }

    // 开启骨架屏检测后通过判断首次与本次骨架屏的差异来判断是否异常
    if (this.skeleton) {
      if (!initial) {
        if (this.skeletons.initial.join('') === this.skeletons.current.join('')) {
          this.report();
        }
      }
      // 关闭骨架屏检测后直接通过空白区域数量来判断是否异常
    } else {
      if (emptyPoints === totalPoints) {
        this.report();
      }
    }
  }

  /**
   * 骨架屏幕模式需要先采样初始的骨架屏节点选择器
   */
  snapshot() {
    if (this.skeleton) {
      this.sampling(true);
    }
  }

  /**
   * 检测当前页面是否渲染异常
   */
  check() {
    if (window.requestIdleCallback) {
      window.requestIdleCallback((deadline) => {
        if (deadline.timeRemaining() > 0) {
          this.sampling();
        }
      });
    } else {
      this.sampling();
    }
  }
}
